import PropTypes from "prop-types";
import {
	useEffect,
	useImperativeHandle,
	useMemo,
	useState,
	memo,
	useCallback,
	forwardRef,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import Loader from "react-loader-spinner";
import {
	flexRender,
	getCoreRowModel,
	getExpandedRowModel,
	getFilteredRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	useReactTable,
} from "@tanstack/react-table";
import { setIsDirty } from "@/redux/actions/isDirty";
import { ReactComponent as IconChevronDown } from "@/assets/svgs/chevron-down.svg";
import { ReactComponent as SortIcon } from "@/assets/svgs/sort-icon.svg";
import { ReactComponent as ErrorInfoCircle } from "@/assets/svgs/exclamation-circle-solid-small.svg";
import EditCell from "./EditCell";
import TableBody from "./TableBody";
import Toolbar from "./Toolbar/index";

const MemoizedEditableCell = memo(
	({ getValue, row, column: { id, columnDef }, table }) => {
		let initialValue = getValue();
		const errors = table.options.meta.errors;
		const [value, setValue] = useState(initialValue);

		const currentError = errors?.find(
			(error) => error.cellId === `${row.id}_${id}`
		);

		const onChange = useCallback(
			(e) => {
				let inputValue = e.target.value;
				
				if (columnDef?.edit?.customOnChange) {
					inputValue = columnDef.edit.customOnChange(inputValue);
				}
				
				table.options?.meta?.updateData(row.id, id, inputValue);
				setValue(inputValue);
			},
			[initialValue, columnDef?.edit?.type, columnDef?.edit?.customOnChange, table, row.id, id]
		);

		if (columnDef?.editable) {
			return (
				<div
					style={{
						display: "flex",
						flexDirection: "column",
						width: "100%",
					}}
				>
					<EditCell
						type="input"
						value={value}
						onChange={onChange}
						onBlur={onChange}
					/>
					{currentError && (
						<div className="DS__error__wrapper">
							<ErrorInfoCircle className="DS__error__icon" />
							<p className="DS__error__message">
								{currentError.errorMessage}
							</p>
						</div>
					)}
				</div>
			);
		}

		return (
			<div
				style={{
					overflow: "hidden",
					textOverflow: "ellipsis",
					...columnDef?.meta?.style,
				}}
			>
				{initialValue}
			</div>
		);
	}
);

MemoizedEditableCell.displayName = "MemoizedEditableCell";

MemoizedEditableCell.propTypes = {
	getValue: PropTypes.func.isRequired,
	row: PropTypes.object.isRequired,
	column: PropTypes.object.isRequired,
	table: PropTypes.object.isRequired,
};

const defaultColumn = {
	cell: MemoizedEditableCell,
};

export const MemoizedTableBody = memo(
	TableBody,
	(prev, next) => prev.table.options.data === next.table.options.data
);

export const InputDataTable = forwardRef(
	(
		{
			data,
			columns,
			isEditable,
			toolbarActions,
			meta = {
				onDataUpdate: () => {},
			},
			showPageSize = false,
			pageSizeOptions = [10, 20, 30],
			showInput,
			rowSelectionIdentifier = "id",
			setSyncSelectedRow,
			renderSubComponent = ({ row }) => {
				return <div>Sub Component</div>;
			},
			sortConfig,
			setSortConfig,
			setSyncChangedValues = () => {},
			isDirtyWarning = false,
			isLoading = false,
			showSelectedActions = false,
			selectedHeaderActions = <div>X item selected</div>,
			saveChangesBtnDisabled = false,
			onRowClick,
			errors,
			...props
		},
		ref
	) => {
		const dispatch = useDispatch();
		const isDirty = useSelector((state) => state?.isDirty);

		// track changedValues
		const [changedValues, setChangedValues] = useState({});

		const [sorting, setSorting] = useState([]);

		useImperativeHandle(ref, () => ({
			changedValues,
			setChangedValues,
			updateData,
			rowSelection,
			setRowSelection,
		}));

		useEffect(() => {
			if (!Array.isArray(data)) {
				throw Error("data must be array");
			}
			data.forEach((row) => {
				if (!row?.id) {
					throw Error("data must have an id");
				}
			});
		}, [data]);

		useEffect(() => {
			if (!isDirtyWarning) return;
			const onBeforeUnload = (e) => {
				if (isDirty) {
					e.preventDefault();
					e.returnValue = "";
				}
			};
			window.addEventListener("beforeunload", onBeforeUnload);
			return () => {
				window.removeEventListener("beforeunload", onBeforeUnload);
			};
		}, [isDirty]);

		const [pagination, setPagination] = useState({
			pageIndex: 0,
			pageSize: 10,
		});

		function handlePageSizeChange(e) {
			setPagination({
				pageIndex: 0,
				pageSize: Number(e.target.value),
			});
		}

		const [rowSelection, setRowSelection] = useState({});

		useEffect(() => {
			setSyncChangedValues(changedValues);
			if (
				changedValues &&
				Object.keys(changedValues)?.length &&
				isDirtyWarning &&
				!isDirty
			) {
				dispatch(setIsDirty(true));
			}
		}, [changedValues]);

		function updateData(rowId, columnId, value) {
			const updatedChangedValues = { ...changedValues };
			const rowChangedValue = updatedChangedValues[rowId] ?? {};

			const hasChangedValue =
				value !== null && value !== rowChangedValue[columnId];

			if (value === null) {
				// if reverted back to the inital value, still maintain in the changedValues
				rowChangedValue[columnId] = rowId;
			} else {
				rowChangedValue[columnId] = value;
			}

			if (hasChangedValue) {
				// Delete the key first to reinsert it at the end, to maintain latest changed index
				delete updatedChangedValues[rowId];

				// Only add back if there is still data in the row
				if (Object.keys(rowChangedValue).length > 0) {
					updatedChangedValues[rowId] = rowChangedValue;
				}
			}

			setChangedValues(updatedChangedValues);
		}

		useEffect(() => {
			if (meta?.onDataUpdate) {
				meta?.onDataUpdate?.(changedValues);
			}
		}, [changedValues]);

		// Sync with external sortConfig if provided
		useEffect(() => {
			if (sortConfig && setSortConfig) {
				setSortConfig(sorting);
			}
		}, [sorting, setSortConfig]);

		const table = useReactTable({
			data,
			columns,
			defaultColumn,
			getCoreRowModel: getCoreRowModel(),
			getFilteredRowModel: getFilteredRowModel(),
			getPaginationRowModel: getPaginationRowModel(),
			getSortedRowModel: getSortedRowModel(),
			state: {
				pagination,
				rowSelection,
				sorting,
			},
			onPaginationChange: setPagination,
			onSortingChange: setSorting,
			getRowId: (row) => row?.[rowSelectionIdentifier],
			getExpandedRowModel: getExpandedRowModel(),
			onRowSelectionChange: setRowSelection,
			manualSorting: false,
			meta: {
				updateData,
				changedValues,
				onRowClick,
				errors,
			},
		});

		table["getIsSomeRowsInPageSelected"] = () => {
			return (
				(data?.some((row) => rowSelection[row.id]) &&
					!data?.every((row) => rowSelection[row.id])) ??
				false
			);
		};

		/**
		 * Column sizing for resize functionality
		 */
		const columnSizeVars = useMemo(() => {
			const headers = table.getFlatHeaders();
			const colSizes = {};
			for (const header of headers) {
				colSizes[`--header-${header.id}-size`] = header.getSize();
				colSizes[`--col-${header.column.id}-size`] =
					header.column.getSize();
			}
			colSizes[`--col-total-size`] = table.getTotalSize();
			return colSizes;
		}, [table.getState().columnSizingInfo]);

		return (
			<div className="DS__data-table3">
				<div className="data-table3__header">
					<Toolbar
						selectedList={Object.keys(rowSelection)}
						actions={selectedHeaderActions}
					/>
				</div>
				<div className="DS__data-table3__body">
					<div
						{...{
							style: {
								...columnSizeVars,
								minWidth: table.getTotalSize(),
								width: "100%",
							},
						}}
					>
						<div className="thead">
							{table.getHeaderGroups().map((headerGroup) => (
								<div
									{...{
										key: headerGroup.id,
										className: "tr",
									}}
								>
									{headerGroup.headers.map(
										(header, headerIndex) => (
											<div
												{...{
													key: header.id,
													className: "th",
													style: {
														minWidth: `calc(var(--header-${header?.id}-size) * 1px)`,
														maxWidth:
															header.column.getCanResize() ||
															header?.column
																?.columnDef
																?.flex
																? undefined
																: header.column.getSize(),
														width: header?.column
															?.columnDef?.flex
															? undefined
															: `calc(var(--header-${header?.id}-size) * 1px)`,
														flex: header?.column
															?.columnDef?.flex
															? `${header.column.columnDef.flex} 1 0`
															: undefined,
													},
												}}
											>
												<div
													className={`DS__data-table__table__head__title ${
														header?.column
															?.columnDef
															?.sortable
															? "data-table__table__head__title__sort-order"
															: ""
													} ${
														{
															asc: "is-asc",
															desc: "is-desc",
														}?.[
															header?.column?.getIsSorted()
														] ?? ""
													}`}
													onClick={
														header?.column
															?.columnDef
															?.sortable
															? header.column.getToggleSortingHandler()
															: null
													}
													onKeyDown={
														header?.column
															?.columnDef
															?.sortable
															? header.column.getToggleSortingHandler()
															: null
													}
													role="none"
												>
													{header.isPlaceholder ? null : (
														<>
															{flexRender(
																header.column
																	.columnDef
																	.header,
																header.getContext()
															)}
															{header?.column
																?.columnDef
																?.sortable && (
																<div
																	className="DS__data_table3_head_icon_sort"
																	data-testid="DS__data-table3-sort"
																>
																	<SortIcon />
																</div>
															)}
														</>
													)}
												</div>
												{headerGroup.headers.length - 1 !== headerIndex && (
													<div className="data_table__input__action__divider"></div>
												)}
											</div>
										)
									)}
								</div>
							))}
						</div>

						{showInput && (
							<div className="tr">
								{table.getAllColumns().map((column) => {
									return (
										<div
											{...{
												key: column.id + "-input",
												className: "th",
												style: {
													minWidth: `calc(var(--header-${column?.id}-size) * 1px)`,
													width: column?.columnDef
														?.flex
														? undefined
														: `calc(var(--header-${column?.id}-size) * 1px)`,
													flex: column?.columnDef
														?.flex
														? `${column.columnDef.flex} 1 0`
														: undefined,
												},
											}}
										>
											{column?.columnDef?.input?.render?.()}
										</div>
									);
								})}
							</div>
						)}

						{isLoading ? (
							<div className="data-table__empty">
								<Loader
									className="loadingSpinner__position__center"
									data-testid="loadingSpinner__position__center"
									type="ThreeDots"
									color="grey"
									height={10}
									width={15}
								/>
							</div>
						) : (
							<TableBody
								table={table}
								renderSubComponent={renderSubComponent}
							/>
						)}
					</div>
				</div>

				{/* Footer: Pagination */}
				{data.length > 0 && (
					<div className="DS__data-table3__footer">
						<div className="DS__data-table3__footer__left">
							Showing {table.getRowModel().rows.length} entries,
							{showPageSize && (
								<div className="DS__select-wrapper">
									<select
										data-testid="pagination-pagesize"
										className="DS__data-table__table__pagination__page"
										type="number"
										value={
											table.getState().pagination.pageSize
										}
										onChange={handlePageSizeChange}
									>
										{pageSizeOptions.map((pageSize) => (
											<option
												data-testid={`pagination-pagesize-option-${pageSize}`}
												key={pageSize}
												value={pageSize}
											>
												{pageSize}
											</option>
										))}
									</select>
									<IconChevronDown className="DS__dropdown-icon" />{" "}
									entries per page
								</div>
							)}
						</div>
					</div>
				)}
			</div>
		);
	}
);

InputDataTable.propTypes = {
	data: PropTypes.array.isRequired,
	columns: PropTypes.array.isRequired,
	isEditable: PropTypes.bool,
	toolbarActions: PropTypes.node,
	meta: PropTypes.shape({
		onDataUpdate: PropTypes.func,
	}),
	showPageSize: PropTypes.bool,
	pageSizeOptions: PropTypes.arrayOf(PropTypes.number),
	showInput: PropTypes.bool,
	rowSelectionIdentifier: PropTypes.string,
	setSyncSelectedRow: PropTypes.func,
	renderSubComponent: PropTypes.func,
	sortConfig: PropTypes.array,
	setSortConfig: PropTypes.func,
	setSyncChangedValues: PropTypes.func,
	isDirtyWarning: PropTypes.bool,
	isLoading: PropTypes.bool,
	showSelectedActions: PropTypes.bool,
	selectedHeaderActions: PropTypes.node,
	saveChangesBtnDisabled: PropTypes.bool,
	onRowClick: PropTypes.func,
	errors: PropTypes.array,
};
